"use client";
import { home_content } from "@/constants/content";
import parse from "html-react-parser";
import warningIcon from "@/assets/icons/warningIcon.svg";
import safetyIcon from "@/assets/icons/safeIcon.svg";
import { ImageCard } from "./ImageCard";
import { InfoCard } from "./InfoCard";
import { Content, Level, LevelSectionProps } from "@/constants/type.constants";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useMediaQuery } from "@react-hook/media-query";
import {
  dataInsightsWrapper,
  desktopSlide,
  imageCardWrapper,
  slideContainer,
  swiperClass,
  textWrapper,
} from "@/styles/cva/home.cva";

function DataInsights({
  category,
  state,
  type = "all",
  variation,
}: LevelSectionProps) {
  
  const IS_MOBILE_VIEW = useMediaQuery("(max-width: 1000px)");
  const content: Content = home_content[category].measures;
  const selectedIndex = type === "us" ? 0 : type === "uk" ? 1 : null;
  const countryCode = type == "us" ? "USA" : "UK";
  const variations = [
    `Air quality data and pollutant measurements for ${state}, ${countryCode} adhere to strict <a href='https://www.epa.gov/outdoor-air-quality-data' target='_blank' rel="nofollow noopener noreferrer">US EPA standards</a>. The data is collected from multiple reliable sources, including satellites and ground-based monitoring stations. This data is further analyzed and enhanced using machine learning and advanced algorithms that factor in elements such as traffic and population density, ensuring accurate and reliable results.`,
    `Air quality measurements for ${state}, ${countryCode} meet strict <a href='https://www.epa.gov/outdoor-air-quality-data' target='_blank' rel="nofollow noopener noreferrer">US EPA standards</a>. These measurements are collected from reliable sources, including satellites and ground-based monitoring systems. Advanced algorithms and machine learning techniques are applied to the data, factoring in variables such as traffic and population density, to ensure precision and reliability.`,
    `Air quality and pollutant data for ${state}, ${countryCode} are sourced from ground-based monitors and satellites, adhering to <a href='https://www.epa.gov/outdoor-air-quality-data' target='_blank' rel="nofollow noopener noreferrer">US EPA standards</a>. Machine learning and advanced algorithms enhance the data by incorporating local variables like traffic and population density, ensuring high accuracy and dependability.`,
    `The air quality data for ${state}, ${countryCode} is gathered from reliable sources such as satellites and monitoring stations, adhering to <a href='https://www.epa.gov/outdoor-air-quality-data' target='_blank' rel="nofollow noopener noreferrer">US EPA guidelines</a>. With the help of advanced algorithms and machine learning, factors like traffic and population density are analyzed to deliver accurate and dependable results.`,
  ];

  const renderInfoCards = (level: Level) => (
    <div className="flex flex-col gap-4 lg:gap-8">
      <InfoCard
        icon={warningIcon}
        text={level.worstText}
        buttonText={level.worst}
        link={level.worstLink}
        iconAlt="Warning icon"
      />
      <InfoCard
        icon={safetyIcon}
        text={level.bestText}
        buttonText={level.best}
        link={level.bestLink}
        iconAlt="Safety icon"
      />
    </div>
  );

  return (
    <div className={dataInsightsWrapper()}>
      <div className={textWrapper()}>
        <h2 className="text-neutral-light-50 font-poppins">
          {parse(
            type !== "all"
              ? `How accurate is <span class='text-primary-50'>${state}, ${countryCode}</span> ${
                  category === "pollen" ? "pollen" : "air quality"
                } data?`
              : content.title
          )}
        </h2>
        <p className="text-sm md:text-base">
          {parse(
            type !== "all"
              ? category === "pollen"
                ? `Ambee’s pollen data for ${state}, ${countryCode} follows <a href='https://pollen.aaaai.org/#/' target='_blank' rel="nofollow noopener noreferrer">National Allergy Bureau (NAB)</a> standards, offering accurate, near real-time updates with clear allergy risk levels. Our proprietary model uses phenology, NDVI, seasonality, weather, wind dispersion patterns, and historical trends to ensure precise, hyper-local pollen forecasts that support allergy management.`
                : variations[variation - 1]
              : content.description
          )}
        </p>
        {category !== "pollen" && type !== "all" && (
          <div className="flex gap-4 items-start font-normal mt-4">
            <p className="text-neutral-light-900">
              <b>Source: </b>
              <a
                className="text-secondary-50"
                href="https://www.getambee.com/sources"
                target="_blank"
                rel="noopener"
              >
                Ambee Data Sources
              </a>
            </p>
          </div>
        )}
      </div>

      <div
        className={imageCardWrapper({
          type: type !== "all" ? "default" : "all",
        })}
      >
        {type !== "all" &&
        selectedIndex !== null &&
        content.levels[selectedIndex] ? (
          <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            <ImageCard
              image={content.levels[selectedIndex].image}
              title={content.levels[selectedIndex].title}
            />
            {renderInfoCards(content.levels[selectedIndex])}
          </div>
        ) : IS_MOBILE_VIEW ? (
          <div className="inline">
            <Swiper
              modules={[Autoplay, Pagination]}
              autoplay={{
                delay: 5000, // Auto slide every 5 seconds
                disableOnInteraction: false, // Keeps autoplay working even after user interaction
              }}
              pagination={{
                clickable: true,
              }}
              loop={true}
              className={swiperClass()}
            >
              {content.levels.map((item, index) => (
                <SwiperSlide key={index}>
                  <div className={slideContainer()}>
                    <ImageCard image={item.image} title={item.title} />
                    {renderInfoCards(item)}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          content.levels.map((item, index) => (
            <div key={index} className={desktopSlide()}>
              <ImageCard image={item.image} title={item.title} />
              {renderInfoCards(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DataInsights;
